/**
 * SignalDetectionPanel — Phase 1 of the Problem-Solving Workflow
 *
 * Two sub-tabs:
 *   1. Signal Report  — intake form: raw complaint, category, community, photos
 *   2. Presenting Concern — AI-cleaned narrative + qualifying questions
 *
 * The raw complaint is stored in `workspace.description`.
 * AI processing creates a `signal_processing` session whose outputPayload
 * contains the clean presenting concern narrative and qualifying questions.
 */

import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import {
  WorkspaceResponse,
  updateWorkspace,
  transitionWorkspacePhase,
} from "../../api/workspaces";
import type { WorkspaceScopeType } from "../../api/workspaces";
import type { AiSessionResponse } from "../../api/workspaceAi";
import {
  fetchAllCommunityEntitiesAdmin,
  fetchAllRelationships,
  communityEntityToSeedShape,
  relationshipToSeedShape,
} from "../../api/communityMetadata";
import { pathDisplay, pathFromLeafGuid } from "../../utils/entityPath";
import { normalizeGuid } from "../../utils/guid";
import { uploadFile } from "../../api/fileStorage";
import { mediaSrc } from "../../api/mediaUrl";
import styles from "./SignalDetectionPanel.module.css";

/* ── Concern-category options (matches projectStandardOptions) ── */
const CONCERN_CATEGORIES = [
  "environmental",
  "health",
  "infrastructure",
  "economic",
  "social",
  "education",
  "governance",
  "safety",
  "other",
] as const;

/* ── Props ── */
interface Props {
  workspace: WorkspaceResponse;
  sessions: AiSessionResponse[];
  onRefreshWorkspace: () => void;
}

/* ── Component ── */
const SignalDetectionPanel: React.FC<Props> = ({
  workspace,
  sessions,
  onRefreshWorkspace,
}) => {
  const { t } = useAppTranslation("pages");

  /* ── Sub-tab state ── */
  const [activeTab, setActiveTab] = useState<"report" | "concern">("report");

  /* ── Form state (seeded from workspace) ── */
  const [workingTitle, setWorkingTitle] = useState(
    workspace.title ?? ""
  );
  const [rawComplaint, setRawComplaint] = useState(
    workspace.description ?? workspace.presentingConcern ?? ""
  );
  const [category, setCategory] = useState(
    workspace.concernCategory ?? ""
  );
  const [scopeType, setScopeType] = useState<WorkspaceScopeType>(
    workspace.scopeType ?? "community"
  );
  const [communityGuid, setCommunityGuid] = useState<string | null>(
    workspace.communityGuid
  );
  const [communityQuery, setCommunityQuery] = useState("");
  const [showCommunitySuggestions, setShowCommunitySuggestions] = useState(false);
  const [communityEntities, setCommunityEntities] = useState<any[]>([]);
  const [communityRelationships, setCommunityRelationships] = useState<any[]>([]);
  const commSearchRef = useRef<HTMLDivElement>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  /* ── Re-seed form state when workspace data arrives after initial mount ── */
  const formSeeded = useRef(false);
  useEffect(() => {
    if (formSeeded.current) return;
    const desc = workspace.description ?? workspace.presentingConcern;
    if (!desc && !workspace.title) return; // nothing to seed yet
    formSeeded.current = true;
    setWorkingTitle(workspace.title ?? "");
    setRawComplaint(desc ?? "");
    setCategory(workspace.concernCategory ?? "");
    setScopeType(workspace.scopeType ?? "community");
    setCommunityGuid(workspace.communityGuid);
  }, [workspace]);

  /* ── Hero photo upload ── */
  const heroFileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const handleChangePhoto = useCallback(() => {
    heroFileRef.current?.click();
  }, []);

  const handleFileSelected = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      setUploading(true);
      try {
        const result = await uploadFile(
          file,
          "heroes",
          workspace.workspaceGuid,
          file.name
        );
        await updateWorkspace(workspace.workspaceGuid, {
          detailHeroImageUrl: result.key,
        });
        onRefreshWorkspace();
      } catch (err) {
        console.error("Hero photo upload failed:", err);
      } finally {
        setUploading(false);
        if (heroFileRef.current) heroFileRef.current.value = "";
      }
    },
    [workspace.workspaceGuid, onRefreshWorkspace]
  );

  /* ── Load community data for search ── */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [ents, rels] = await Promise.all([
          fetchAllCommunityEntitiesAdmin(),
          fetchAllRelationships(),
        ]);
        if (cancelled) return;
        if (Array.isArray(ents) && ents.length > 0)
          setCommunityEntities(ents.map(communityEntityToSeedShape));
        if (Array.isArray(rels) && rels.length > 0)
          setCommunityRelationships(rels.map(relationshipToSeedShape));
      } catch {
        /* fallback to empty — no community search available */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  /* Close dropdown on outside click */
  useEffect(() => {
    if (!showCommunitySuggestions) return;
    const handler = (e: MouseEvent) => {
      if (commSearchRef.current && !commSearchRef.current.contains(e.target as Node))
        setShowCommunitySuggestions(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showCommunitySuggestions]);

  /* ── Geo entity index for typeahead ── */
  const isGeoEntity = (e: any): boolean =>
    String(e?.entity_type ?? "").toLowerCase().startsWith("geo_");

  const geoIndex = useMemo(() => {
    const out: { guid: string; display: string; search: string; leafName: string }[] = [];
    if (!communityEntities.length) return out;
    for (const e of communityEntities) {
      if (!isGeoEntity(e)) continue;
      const g = normalizeGuid(e?.entity_guid);
      if (!g) continue;
      const leafName = String(e?.name ?? "").trim();
      const pathEntities = pathFromLeafGuid(g, communityEntities, communityRelationships);
      const pathNames = pathEntities.map((pe: any) => String(pe?.name ?? "").trim());
      const display = pathNames.length > 0 ? pathDisplay(pathNames) : leafName;
      if (!display) continue;
      out.push({ guid: g, display, search: `${leafName} ${display}`.toLowerCase(), leafName });
    }
    out.sort((a, b) => a.display.localeCompare(b.display));
    return out;
  }, [communityEntities, communityRelationships]);

  const communitySuggestions = useMemo(() => {
    const q = communityQuery.trim().toLowerCase();
    if (!q || q.length < 2) return [];
    const scored: { guid: string; display: string; score: number }[] = [];
    for (const row of geoIndex) {
      const name = row.leafName.toLowerCase();
      let score = -1;
      if (name.startsWith(q)) score = 100;
      else if (row.search.startsWith(q)) score = 90;
      else if (row.search.includes(` ${q}`)) score = 70;
      else if (row.search.includes(q)) score = 50;
      if (score < 0) continue;
      scored.push({ guid: row.guid, display: row.display, score: score + Math.min(20, row.display.split(",").length) });
    }
    scored.sort((a, b) => b.score - a.score || a.display.localeCompare(b.display));
    return scored.slice(0, 10);
  }, [communityQuery, geoIndex]);

  /* ── Resolve selected community display name ── */
  const selectedCommunityLabel = useMemo(() => {
    if (!communityGuid) return null;
    const match = geoIndex.find((r) => r.guid.toUpperCase() === communityGuid.toUpperCase());
    return match?.display ?? null;
  }, [communityGuid, geoIndex]);

  /* ── Locate the most recent signal_processing session ── */
  const signalSession = useMemo(() => {
    const sorted = sessions
      .filter((s) => s.sessionType === "signal_processing" && s.status === "completed")
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    return sorted[0] ?? null;
  }, [sessions]);

  /* ── Parse the AI output ── */
  const aiOutput = useMemo(() => {
    if (!signalSession?.outputPayload) return null;
    try {
      const parsed =
        typeof signalSession.outputPayload === "string"
          ? JSON.parse(signalSession.outputPayload)
          : signalSession.outputPayload;
      return parsed as {
        presenting_concern_title?: string;
        presenting_concern_narrative?: string;
        concern_category?: string;
        severity_assessment?: {
          level?: string;
          rationale?: string;
        };
        clarifying_questions?: Array<{
          question: string;
          why_important: string;
        }>;
        pii_flags?: string[];
        confidence?: {
          score?: number;
          rationale?: string;
        };
      };
    } catch {
      return null;
    }
  }, [signalSession]);

  /* ── Has the AI produced output? ── */
  const hasConcern = !!aiOutput?.presenting_concern_narrative;

  /* ── Editable fields for review ── */
  const [editConcernTitle, setEditConcernTitle] = useState("");
  const [editConcernNarrative, setEditConcernNarrative] = useState("");
  const [editInitialAnalysis, setEditInitialAnalysis] = useState("");
  const [validating, setValidating] = useState(false);
  const [validated, setValidated] = useState(!!workspace.presentingConcernValidatedAt);
  const reviewSeeded = useRef(false);

  // Seed editable fields from AI output or existing workspace data
  useEffect(() => {
    if (reviewSeeded.current) return;
    if (workspace.presentingConcernValidatedAt) {
      // Already validated — use stored values
      setEditConcernTitle(workspace.title ?? "");
      setEditConcernNarrative(workspace.presentingConcern ?? "");
      setEditInitialAnalysis(workspace.initialAnalysis ?? "");
      reviewSeeded.current = true;
    } else if (aiOutput) {
      setEditConcernTitle(aiOutput.presenting_concern_title ?? workspace.title ?? "");
      setEditConcernNarrative(aiOutput.presenting_concern_narrative ?? "");
      // Use severity rationale as initial analysis if no dedicated field
      setEditInitialAnalysis(
        aiOutput.severity_assessment?.rationale ?? ""
      );
      reviewSeeded.current = true;
    }
  }, [aiOutput, workspace]);

  /* ── Validate & Complete Signal Detection ── */
  const handleValidateAndComplete = useCallback(async () => {
    if (!editConcernNarrative.trim()) return;
    setValidating(true);
    try {
      // Save validated presenting concern + initial analysis to workspace
      await updateWorkspace(workspace.workspaceGuid, {
        title: editConcernTitle.trim() || undefined,
        presentingConcern: editConcernNarrative.trim(),
        initialAnalysis: editInitialAnalysis.trim() || undefined,
      });
      // Transition phase to detection_validation
      await transitionWorkspacePhase(workspace.workspaceGuid, {
        toPhase: "detection_validation",
        rationale: "Signal Detection validated — Presenting Concern and Initial Analysis approved by team.",
      });
      setValidated(true);
      onRefreshWorkspace();
    } catch (err) {
      console.error("Validation failed:", err);
    } finally {
      setValidating(false);
    }
  }, [workspace.workspaceGuid, editConcernTitle, editConcernNarrative, editInitialAnalysis, onRefreshWorkspace]);

  /* ── Save raw signal to workspace ── */
  const handleSave = useCallback(async () => {
    setSaving(true);
    setSaved(false);
    try {
      await updateWorkspace(workspace.workspaceGuid, {
        title: workingTitle.trim() || undefined,
        description: rawComplaint,
        concernCategory: category || undefined,
        scopeType,
        communityGuid: scopeType === "community" ? (communityGuid ?? undefined) : undefined,
      });
      onRefreshWorkspace();
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {
      // Error already visible via workspace refresh
    } finally {
      setSaving(false);
    }
  }, [workspace.workspaceGuid, workingTitle, rawComplaint, category, scopeType, communityGuid, onRefreshWorkspace]);

  /* ── Hero image ── */
  const heroKey = workspace.detailHeroImageUrl ?? workspace.heroImageUrl;
  const heroSrc = mediaSrc(heroKey);

  /* ── Sub-tab definitions ── */
  const TABS = useMemo(
    () => [
      { key: "report" as const, label: t("workspace.signal.tabReport") },
      { key: "concern" as const, label: t("workspace.signal.tabConcern") },
    ],
    [t]
  );

  return (
    <div className={styles.container}>
      {/* ── Sub-tab bar ── */}
      <div className={styles.subTabBar}>
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={
              activeTab === tab.key ? styles.subTabActive : styles.subTab
            }
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
            {tab.key === "concern" && hasConcern && (
              <span className={styles.tabBadge}>AI</span>
            )}
          </button>
        ))}
      </div>

      {/* ══════════════════════════════════════════════════════
          TAB 1 — Signal Report (intake form)
          ══════════════════════════════════════════════════════ */}
      {activeTab === "report" && (
        <div className={styles.reportTab}>
          {/* ── Working title ── */}
          <section className={styles.section}>
            <label className={styles.sectionLabel}>
              {t("workspace.signal.titleLabel")}
            </label>
            <p className={styles.sectionHint}>
              {t("workspace.signal.titleHint")}
            </p>
            <input
              type="text"
              className={styles.titleInput}
              value={workingTitle}
              onChange={(e) => setWorkingTitle(e.target.value)}
              placeholder={t("workspace.signal.titlePlaceholder")}
              maxLength={200}
            />
          </section>

          {/* ── Raw complaint ── */}
          <section className={styles.section}>
            <label className={styles.sectionLabel}>
              {t("workspace.signal.rawComplaintLabel")}
            </label>
            <p className={styles.sectionHint}>
              {t("workspace.signal.rawComplaintHint")}
            </p>
            <textarea
              className={styles.textarea}
              rows={6}
              value={rawComplaint}
              onChange={(e) => setRawComplaint(e.target.value)}
              placeholder={t("workspace.signal.rawComplaintPlaceholder")}
            />
          </section>

          {/* ── Problem category ── */}
          <section className={styles.section}>
            <label className={styles.sectionLabel}>
              {t("workspace.signal.categoryLabel")}
            </label>
            <select
              className={styles.select}
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              <option value="">{t("workspace.signal.categoryPlaceholder")}</option>
              {CONCERN_CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>
                  {t(`workspace.signal.categories.${cat}`)}
                </option>
              ))}
            </select>
          </section>

          {/* ── Scope / Community ── */}
          <section className={styles.section}>
            <label className={styles.sectionLabel}>
              {t("workspace.signal.scopeLabel")}
            </label>
            <p className={styles.sectionHint}>
              {t("workspace.signal.scopeHint")}
            </p>
            <div className={styles.scopePills}>
              {(["personal", "household", "community"] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  className={scopeType === s ? styles.scopePillActive : styles.scopePill}
                  onClick={() => {
                    setScopeType(s);
                    if (s !== "community") {
                      setCommunityGuid(null);
                      setCommunityQuery("");
                    }
                  }}
                >
                  {t(`workspace.signal.scope_${s}`)}
                </button>
              ))}
            </div>

            {/* Community search (only when scope is community) */}
            {scopeType === "community" && (
              <div className={styles.commSearchWrap} ref={commSearchRef}>
                <div className={styles.commTokenInput}>
                  {communityGuid && selectedCommunityLabel && (
                    <div className={styles.commToken}>
                      <span className={styles.commTokenText}>{selectedCommunityLabel}</span>
                      <button
                        type="button"
                        className={styles.commTokenClear}
                        onClick={() => {
                          setCommunityGuid(null);
                          setCommunityQuery("");
                          setShowCommunitySuggestions(false);
                        }}
                        aria-label={t("workspace.signal.clearCommunity")}
                      >
                        ×
                      </button>
                    </div>
                  )}
                  <input
                    type="text"
                    className={styles.commSearchInput}
                    value={communityQuery}
                    placeholder={
                      communityGuid
                        ? t("workspace.signal.communityChangePlaceholder")
                        : t("workspace.signal.communitySearchPlaceholder")
                    }
                    onChange={(e) => {
                      const v = e.target.value;
                      if (communityGuid && v.trim().length > 0) {
                        setCommunityGuid(null);
                      }
                      setCommunityQuery(v);
                      setShowCommunitySuggestions(true);
                    }}
                    onFocus={() => setShowCommunitySuggestions(true)}
                  />
                </div>
                {showCommunitySuggestions && communitySuggestions.length > 0 && (
                  <div className={styles.commSuggestions}>
                    {communitySuggestions.map((s) => (
                      <div
                        key={s.guid}
                        className={styles.commSuggestionItem}
                        onMouseDown={() => {
                          setCommunityGuid(s.guid);
                          setCommunityQuery("");
                          setShowCommunitySuggestions(false);
                        }}
                      >
                        {s.display}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </section>

          {/* ── Photo / Hero ── */}
          <section className={styles.section}>
            <label className={styles.sectionLabel}>
              {t("workspace.signal.photosLabel")}
            </label>
            <input
              ref={heroFileRef}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleFileSelected}
            />
            <div className={styles.photoArea}>
              {heroSrc ? (
                <>
                  <img
                    src={heroSrc}
                    alt={t("workspace.signal.heroAlt")}
                    className={styles.photoPreview}
                  />
                  <button
                    type="button"
                    className={styles.photoChangeBtn}
                    onClick={handleChangePhoto}
                    disabled={uploading}
                  >
                    {uploading
                      ? t("workspace.signal.uploading")
                      : t("workspace.signal.changePhoto")}
                  </button>
                </>
              ) : (
                <div
                  className={styles.photoPlaceholder}
                  onClick={handleChangePhoto}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === "Enter" && handleChangePhoto()}
                >
                  <span className={styles.photoIcon}>📷</span>
                  <span>
                    {uploading
                      ? t("workspace.signal.uploading")
                      : t("workspace.signal.photoPlaceholder")}
                  </span>
                </div>
              )}
            </div>
          </section>

          {/* ── Save + Submit row ── */}
          <div className={styles.actionRow}>
            <button
              type="button"
              className={styles.saveBtn}
              onClick={handleSave}
              disabled={saving}
            >
              {saving
                ? t("workspace.signal.saving")
                : saved
                ? t("workspace.signal.saved")
                : t("workspace.signal.saveSignal")}
            </button>
            <span className={styles.actionHint}>
              {t("workspace.signal.submitHint")}
            </span>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════
          TAB 2 — Presenting Concern & Initial Analysis (Review)
          ══════════════════════════════════════════════════════ */}
      {activeTab === "concern" && (
        <div className={styles.concernTab}>
          {!hasConcern && !validated ? (
            /* ── Empty state ── */
            <div className={styles.emptyState}>
              <span className={styles.emptyIcon}>🔬</span>
              <p className={styles.emptyTitle}>
                {t("workspace.signal.noConcernTitle")}
              </p>
              <p className={styles.emptyDesc}>
                {t("workspace.signal.noConcernDesc")}
              </p>
            </div>
          ) : (
            <>
              {/* ── Validated badge ── */}
              {validated && (
                <div className={styles.validatedBanner}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  <span>{t("workspace.signal.validatedBanner")}</span>
                </div>
              )}

              {/* ── AI badge ── */}
              {hasConcern && !validated && (
                <div className={styles.reviewHint}>
                  <span className={styles.aiBadge}>
                    {t("workspace.signal.aiGenerated")}
                  </span>
                  <span className={styles.reviewHintText}>
                    {t("workspace.signal.reviewHint")}
                  </span>
                </div>
              )}

              {/* ── Presenting Concern Title ── */}
              <section className={styles.section}>
                <label className={styles.sectionLabel}>
                  {t("workspace.signal.concernTitleLabel")}
                </label>
                <input
                  type="text"
                  className={styles.titleInput}
                  value={editConcernTitle}
                  onChange={(e) => setEditConcernTitle(e.target.value)}
                  disabled={validated}
                  maxLength={300}
                />
              </section>

              {/* ── Presenting Concern Narrative ── */}
              <section className={styles.section}>
                <label className={styles.sectionLabel}>
                  {t("workspace.signal.concernNarrativeLabel")}
                </label>
                <textarea
                  className={styles.textarea}
                  rows={6}
                  value={editConcernNarrative}
                  onChange={(e) => setEditConcernNarrative(e.target.value)}
                  disabled={validated}
                />
              </section>

              {/* ── Initial Analysis ── */}
              <section className={styles.section}>
                <label className={styles.sectionLabel}>
                  {t("workspace.signal.initialAnalysisLabel")}
                </label>
                <textarea
                  className={styles.textarea}
                  rows={4}
                  value={editInitialAnalysis}
                  onChange={(e) => setEditInitialAnalysis(e.target.value)}
                  disabled={validated}
                />
              </section>

              {/* ── Severity + Category (read-only from AI) ── */}
              {aiOutput?.severity_assessment && (
                <section className={styles.severitySection}>
                  <div className={styles.severityHeader}>
                    <span className={styles.sectionLabel}>
                      {t("workspace.signal.severityLabel")}
                    </span>
                    <span
                      className={`${styles.severityBadge} ${
                        styles[
                          `severity${
                            (aiOutput.severity_assessment.level ?? "unknown")
                              .charAt(0)
                              .toUpperCase() +
                            (aiOutput.severity_assessment.level ?? "unknown").slice(1)
                          }`
                        ] ?? ""
                      }`}
                    >
                      {aiOutput.severity_assessment.level}
                    </span>
                  </div>
                </section>
              )}

              {/* ── Confidence ── */}
              {aiOutput?.confidence && (
                <section className={styles.confidenceSection}>
                  <span className={styles.sectionLabel}>
                    {t("workspace.signal.confidenceLabel")}
                  </span>
                  <div className={styles.confidenceBar}>
                    <div
                      className={styles.confidenceFill}
                      style={{
                        width: `${(aiOutput.confidence.score ?? 0) * 100}%`,
                      }}
                    />
                  </div>
                </section>
              )}

              {/* ── Validate & Complete Signal Detection ── */}
              {!validated && (
                <div className={styles.actionRow}>
                  <button
                    type="button"
                    className={styles.validateBtn}
                    onClick={handleValidateAndComplete}
                    disabled={validating || !editConcernNarrative.trim()}
                  >
                    {validating
                      ? t("workspace.signal.validating")
                      : t("workspace.signal.validateAndComplete")}
                  </button>
                  <span className={styles.actionHint}>
                    {t("workspace.signal.validateHint")}
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SignalDetectionPanel;
